/**
 * Material Issue Request Hook
 * 
 * Handles CRUD operations for material issue requests from production to inventory.
 * Includes request creation, approval, rejection, and issuance workflows.
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/hooks/useToast';
import { useAuthStore } from '@/store/authStore';
import { notifyInventory } from '@/lib/notifications/notificationService';
import { TEMPLATE_MATERIAL_ISSUE_REQUESTED, TEMPLATE_MATERIAL_ISSUE_APPROVED } from '@/lib/notifications/templateCodes';
import type {
  MaterialIssueRequest,
  MaterialIssueRequestFilters,
  MaterialIssueRequestStats,
  CreateMaterialIssueRequestInput,
  ApproveMaterialIssueRequestInput,
  RejectMaterialIssueRequestInput,
  IssueMaterialRequestInput,
} from '@/types/materialIssueRequest';

export function useMaterialIssueRequests() {
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState<MaterialIssueRequest[]>([]);
  const [stats, setStats] = useState<MaterialIssueRequestStats | null>(null);
  const { toast } = useToast();
  const user = useAuthStore((state) => state.user);

  /**
   * Generate a new request number
   */
  const generateRequestNumber = async (): Promise<string> => {
    const { data, error } = await supabase.rpc('generate_mir_number');
    if (error) {
      console.error('Error generating request number:', error);
      // Fallback to timestamp-based number
      return `MIR-${Date.now()}`;
    }
    return data;
  };

  /**
   * Fetch material issue requests with optional filters
   */
  const fetchRequests = useCallback(async (
    filters?: MaterialIssueRequestFilters
  ): Promise<MaterialIssueRequest[]> => {
    try {
      setLoading(true);

      let query = supabase
        .from('material_issue_request')
        .select(`
          *,
          work_order:work_order_id(id, work_order_number),
          from_warehouse:from_warehouse_id(id, name, code),
          to_warehouse:to_warehouse_id(id, name, code),
          requester:requested_by(id, full_name),
          approver:approved_by(id, full_name),
          rejecter:rejected_by(id, full_name),
          issuer:issued_by(id, full_name)
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        if (Array.isArray(filters.status)) {
          query = query.in('status', filters.status);
        } else {
          query = query.eq('status', filters.status);
        }
      }
      if (filters?.priority) {
        query = query.eq('priority', filters.priority);
      }
      if (filters?.work_order_id) {
        query = query.eq('work_order_id', filters.work_order_id);
      }
      if (filters?.from_warehouse_id) {
        query = query.eq('from_warehouse_id', filters.from_warehouse_id);
      }
      if (filters?.requested_by) {
        query = query.eq('requested_by', filters.requested_by);
      }
      if (filters?.from_date) {
        query = query.gte('created_at', filters.from_date);
      }
      if (filters?.to_date) {
        query = query.lte('created_at', filters.to_date);
      }
      if (filters?.search) {
        query = query.or(`request_number.ilike.%${filters.search}%,item_name.ilike.%${filters.search}%,item_code.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Map the joined data
      const mappedData: MaterialIssueRequest[] = (data || []).map((r: any) => ({
        ...r,
        work_order_number: r.work_order?.work_order_number,
        from_warehouse_name: r.from_warehouse?.name,
        from_warehouse_code: r.from_warehouse?.code,
        to_warehouse_name: r.to_warehouse?.name,
        requested_by_name: r.requester?.full_name,
        approved_by_name: r.approver?.full_name,
        rejected_by_name: r.rejecter?.full_name,
        issued_by_name: r.issuer?.full_name,
      }));

      setRequests(mappedData);
      return mappedData;
    } catch (error: any) {
      console.error('Error fetching material issue requests:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to fetch material issue requests',
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Fetch stats for dashboard
   */
  const fetchStats = useCallback(async (): Promise<MaterialIssueRequestStats> => {
    try {
      const { data, error } = await supabase
        .from('material_issue_request')
        .select('status, priority');

      if (error) throw error;

      const stats: MaterialIssueRequestStats = {
        total_pending: 0,
        total_approved: 0,
        total_rejected: 0,
        total_issued: 0,
        urgent_pending: 0,
        high_priority_pending: 0,
      };

      (data || []).forEach((r: any) => {
        switch (r.status) {
          case 'PENDING':
            stats.total_pending++;
            if (r.priority === 'URGENT') stats.urgent_pending++;
            if (r.priority === 'HIGH') stats.high_priority_pending++;
            break;
          case 'APPROVED':
            stats.total_approved++;
            break;
          case 'REJECTED':
            stats.total_rejected++;
            break;
          case 'ISSUED':
            stats.total_issued++;
            break;
        }
      });

      setStats(stats);
      return stats;
    } catch (error: any) {
      console.error('Error fetching stats:', error);
      return {
        total_pending: 0,
        total_approved: 0,
        total_rejected: 0,
        total_issued: 0,
        urgent_pending: 0,
        high_priority_pending: 0,
      };
    }
  }, []);

  /**
   * Create a new material issue request
   */
  const createRequest = useCallback(async (
    input: CreateMaterialIssueRequestInput
  ): Promise<MaterialIssueRequest | null> => {
    if (!user?.id) {
      toast({
        title: 'Error',
        description: 'You must be logged in to create a request',
        variant: 'destructive',
      });
      return null;
    }

    try {
      setLoading(true);

      // Generate request number
      const requestNumber = await generateRequestNumber();

      const { data, error } = await supabase
        .from('material_issue_request')
        .insert({
          request_number: requestNumber,
          work_order_id: input.work_order_id,
          work_order_material_id: input.work_order_material_id,
          item_id: input.item_id,
          item_code: input.item_code,
          item_name: input.item_name,
          material_type: input.material_type,
          raw_material_size: input.raw_material_size,
          requested_qty: input.requested_qty,
          uom: input.uom,
          from_warehouse_id: input.from_warehouse_id,
          to_warehouse_id: input.to_warehouse_id,
          priority: input.priority || 'NORMAL',
          notes: input.notes,
          requested_by: user.id,
          status: 'PENDING',
        })
        .select()
        .single();

      if (error) throw error;

      // Queue email notification for material issue request
      notifyInventory(
        TEMPLATE_MATERIAL_ISSUE_REQUESTED,
        {
          request_number: requestNumber,
          item: input.item_name || input.item_code || 'N/A',
          qty: input.requested_qty,
          work_order: input.work_order_id || 'N/A',
          priority: input.priority || 'NORMAL',
        },
        'material_issue_request',
        data?.id
      );

      toast({
        title: 'Request Created',
        description: `Material issue request ${requestNumber} has been submitted for approval`,
      });

      return data;
    } catch (error: any) {
      console.error('Error creating material issue request:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create material issue request',
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  /**
   * Approve a material issue request
   */
  const approveRequest = useCallback(async (
    input: ApproveMaterialIssueRequestInput
  ): Promise<boolean> => {
    if (!user?.id) {
      toast({
        title: 'Error',
        description: 'You must be logged in to approve requests',
        variant: 'destructive',
      });
      return false;
    }

    try {
      setLoading(true);

      // Get the request first
      const { data: request, error: fetchError } = await supabase
        .from('material_issue_request')
        .select('*')
        .eq('id', input.request_id)
        .single();

      if (fetchError) throw fetchError;

      if (request.status !== 'PENDING') {
        toast({
          title: 'Error',
          description: 'Only pending requests can be approved',
          variant: 'destructive',
        });
        return false;
      }

      const approvedQty = input.approved_qty || request.requested_qty;

      const { error } = await supabase
        .from('material_issue_request')
        .update({
          status: 'APPROVED',
          approved_by: user.id,
          approved_at: new Date().toISOString(),
          approved_qty: approvedQty,
          approval_notes: input.approval_notes,
        })
        .eq('id', input.request_id);

      if (error) throw error;

      // Queue email notification for material issue approval
      notifyInventory(
        TEMPLATE_MATERIAL_ISSUE_APPROVED,
        {
          request_number: request.request_number || input.request_id,
          approved_qty: approvedQty,
          item: request.item_name || request.item_code || 'N/A',
        },
        'material_issue_request',
        input.request_id
      );

      toast({
        title: 'Request Approved',
        description: `Material issue request has been approved for ${approvedQty} units`,
      });

      return true;
    } catch (error: any) {
      console.error('Error approving request:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to approve request',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  /**
   * Reject a material issue request
   */
  const rejectRequest = useCallback(async (
    input: RejectMaterialIssueRequestInput
  ): Promise<boolean> => {
    if (!user?.id) {
      toast({
        title: 'Error',
        description: 'You must be logged in to reject requests',
        variant: 'destructive',
      });
      return false;
    }

    try {
      setLoading(true);

      const { error } = await supabase
        .from('material_issue_request')
        .update({
          status: 'REJECTED',
          rejected_by: user.id,
          rejected_at: new Date().toISOString(),
          rejection_reason: input.rejection_reason,
        })
        .eq('id', input.request_id)
        .eq('status', 'PENDING'); // Only reject pending requests

      if (error) throw error;

      toast({
        title: 'Request Rejected',
        description: 'Material issue request has been rejected',
      });

      return true;
    } catch (error: any) {
      console.error('Error rejecting request:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to reject request',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  /**
   * Issue material (after approval)
   * Creates inventory transaction and updates work order material
   */
  const issueMaterial = useCallback(async (
    input: IssueMaterialRequestInput
  ): Promise<boolean> => {
    if (!user?.id) {
      toast({
        title: 'Error',
        description: 'You must be logged in to issue materials',
        variant: 'destructive',
      });
      return false;
    }

    try {
      setLoading(true);

      // Get the request
      const { data: request, error: fetchError } = await supabase
        .from('material_issue_request')
        .select('*')
        .eq('id', input.request_id)
        .single();

      if (fetchError) throw fetchError;

      if (request.status !== 'APPROVED') {
        toast({
          title: 'Error',
          description: 'Only approved requests can be issued',
          variant: 'destructive',
        });
        return false;
      }

      const issuedQty = input.issued_qty || request.approved_qty || request.requested_qty;

      // Find inventory item if not directly linked
      let inventoryItemId = request.item_id;
      
      if (!inventoryItemId && request.material_type) {
        // Search for inventory items matching the material type
        const materialType = request.material_type.trim();
        const { data: matchingItems } = await supabase
          .from('inventory_items')
          .select('id, code, name, specifications')
          .eq('is_active', true)
          .or(`name.ilike.%${materialType}%,code.ilike.%${materialType.replace(/\s+/g, '')}%`);
        
        if (matchingItems && matchingItems.length > 0) {
          // Try to find best match - check specifications.material field
          const bestMatch = matchingItems.find(item => {
            const specs = item.specifications as any;
            return specs?.material?.toLowerCase().includes(materialType.toLowerCase());
          }) || matchingItems[0];
          
          inventoryItemId = bestMatch.id;
          console.log('Found matching inventory item for issue:', bestMatch.name);
        }
      }

      if (!inventoryItemId) {
        toast({
          title: 'Error',
          description: 'No inventory item found for this material type. Please link an inventory item first.',
          variant: 'destructive',
        });
        return false;
      }

      // Create inventory transaction
      // Note: item_id references bom_components, inventory_item_id references inventory_items
      // For material issue, we only need inventory_item_id (the raw material being issued)
      const { data: txData, error: txError } = await supabase
        .from('inventory_tx')
        .insert({
          txn_type: 'ISSUE-TO-PRODUCTION',
          inventory_item_id: inventoryItemId,
          qty: issuedQty,
          uom: request.uom,
          wh_from: request.from_warehouse_id,
          wh_to: request.to_warehouse_id,
          ref_table: 'material_issue_request',
          ref_id: request.id,
          note: `Material issue for WO material. Request: ${request.request_number}`,
          created_by: user.id,
        })
        .select()
        .single();

      if (txError) throw txError;

      // Update warehouse stock - decrease from source warehouse
      // Get current stock first
      const { data: currentStock, error: stockFetchError } = await supabase
        .from('warehouse_stock')
        .select('id, quantity, reserved_quantity, available_quantity')
        .eq('warehouse_id', request.from_warehouse_id)
        .eq('item_id', inventoryItemId)
        .single();

      if (stockFetchError) {
        console.warn('No warehouse stock record found, skipping stock update');
      } else if (currentStock) {
        const newQuantity = Math.max(0, (parseFloat(currentStock.quantity as string) || 0) - issuedQty);
        const newAvailable = Math.max(0, (parseFloat(currentStock.available_quantity as string) || 0) - issuedQty);
        
        const { error: stockUpdateError } = await supabase
          .from('warehouse_stock')
          .update({ 
            quantity: newQuantity,
            available_quantity: newAvailable,
            last_transaction_id: txData.id,
            last_transaction_date: new Date().toISOString(),
          })
          .eq('id', currentStock.id);

        if (stockUpdateError) {
          console.error('Error updating warehouse stock:', stockUpdateError);
        }
      }

      // Also update inventory_items.stock_level
      const { data: itemData } = await supabase
        .from('inventory_items')
        .select('stock_level')
        .eq('id', inventoryItemId)
        .single();

      if (itemData) {
        const newStockLevel = Math.max(0, (parseFloat(itemData.stock_level as string) || 0) - issuedQty);
        await supabase
          .from('inventory_items')
          .update({ stock_level: newStockLevel })
          .eq('id', inventoryItemId);
      }

      // Update work order material issued quantity
      const { data: womData } = await supabase
        .from('work_order_materials')
        .select('issued_quantity')
        .eq('id', request.work_order_material_id)
        .single();

      const newIssuedQty = (womData?.issued_quantity || 0) + issuedQty;

      await supabase
        .from('work_order_materials')
        .update({ 
          issued_quantity: newIssuedQty,
          status: 'Partially Issued', // Will be updated to Fully Issued if needed
        })
        .eq('id', request.work_order_material_id);

      // Update the request status to ISSUED
      const { error: updateError } = await supabase
        .from('material_issue_request')
        .update({
          status: 'ISSUED',
          issued_by: user.id,
          issued_at: new Date().toISOString(),
          issued_qty: issuedQty,
          inventory_tx_id: txData.id,
        })
        .eq('id', input.request_id);

      if (updateError) throw updateError;

      toast({
        title: 'Material Issued',
        description: `${issuedQty} ${request.uom} has been issued to production`,
      });

      return true;
    } catch (error: any) {
      console.error('Error issuing material:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to issue material',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  /**
   * Cancel a request (by requester)
   */
  const cancelRequest = useCallback(async (requestId: string): Promise<boolean> => {
    if (!user?.id) {
      toast({
        title: 'Error',
        description: 'You must be logged in to cancel requests',
        variant: 'destructive',
      });
      return false;
    }

    try {
      setLoading(true);

      const { error } = await supabase
        .from('material_issue_request')
        .update({ status: 'CANCELLED' })
        .eq('id', requestId)
        .in('status', ['PENDING', 'APPROVED']); // Can only cancel pending or approved

      if (error) throw error;

      toast({
        title: 'Request Cancelled',
        description: 'Material issue request has been cancelled',
      });

      return true;
    } catch (error: any) {
      console.error('Error cancelling request:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to cancel request',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  /**
   * Get a single request by ID
   */
  const getRequest = useCallback(async (requestId: string): Promise<MaterialIssueRequest | null> => {
    try {
      const { data, error } = await supabase
        .from('material_issue_request')
        .select(`
          *,
          work_order:work_order_id(id, work_order_number),
          from_warehouse:from_warehouse_id(id, name, code),
          to_warehouse:to_warehouse_id(id, name, code),
          requester:requested_by(id, full_name),
          approver:approved_by(id, full_name),
          rejecter:rejected_by(id, full_name),
          issuer:issued_by(id, full_name)
        `)
        .eq('id', requestId)
        .single();

      if (error) throw error;

      return {
        ...data,
        work_order_number: data.work_order?.work_order_number,
        from_warehouse_name: data.from_warehouse?.name,
        from_warehouse_code: data.from_warehouse?.code,
        to_warehouse_name: data.to_warehouse?.name,
        requested_by_name: data.requester?.full_name,
        approved_by_name: data.approver?.full_name,
        rejected_by_name: data.rejecter?.full_name,
        issued_by_name: data.issuer?.full_name,
      };
    } catch (error: any) {
      console.error('Error fetching request:', error);
      return null;
    }
  }, []);

  return {
    loading,
    requests,
    stats,
    fetchRequests,
    fetchStats,
    createRequest,
    approveRequest,
    rejectRequest,
    issueMaterial,
    cancelRequest,
    getRequest,
  };
}
